import { format } from 'date-fns'
import { ja } from 'date-fns/locale'
import { ArrowLeft, ArrowRight, CalendarDays } from 'lucide-react'
import { useState } from 'react'
import type { Matcher } from 'react-day-picker'

import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { formatRelativeDate } from '@/lib/format-utils'

type DateMarker = {
  /** 日付の下に点を付ける日 */
  matcher: Matcher
  /** カレンダーの下に出す凡例 */
  label: string
}

type DateNavigatorProps = {
  date: Date
  onDateChange: (date: Date) => void
  marker?: DateMarker
}

const MARKER_CLASS_NAME =
  'after:pointer-events-none after:absolute after:bottom-1 after:left-1/2 after:size-1 after:-translate-x-1/2 after:rounded-full after:bg-emerald-500'

/**
 * 日付ナビゲーションコンポーネント。
 * 左右矢印で前後日移動、タップでカレンダーポップオーバー、今日ボタン付き。
 */
export const DateNavigator = ({ date, onDateChange, marker }: DateNavigatorProps) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const today = new Date()
  const todayStr = format(today, 'yyyy-MM-dd')
  const isToday = format(date, 'yyyy-MM-dd') === todayStr

  const goToPreviousDay = () => {
    const prev = new Date(date)
    prev.setDate(prev.getDate() - 1)
    onDateChange(prev)
  }

  const goToNextDay = () => {
    const next = new Date(date)
    next.setDate(next.getDate() + 1)
    if (format(next, 'yyyy-MM-dd') <= todayStr) {
      onDateChange(next)
    }
  }

  const handleDateSelect = (selected: Date | undefined) => {
    if (selected) {
      onDateChange(selected)
      setIsCalendarOpen(false)
    }
  }

  const handleGoToToday = () => {
    onDateChange(new Date())
    setIsCalendarOpen(false)
  }

  return (
    <div className="flex items-center gap-1">
      <Button variant="ghost" size="icon" className="size-8" onClick={goToPreviousDay}>
        <ArrowLeft className="size-4" />
      </Button>
      <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" className="h-auto px-3 py-1.5 gap-2">
            {formatRelativeDate(date)}
            <CalendarDays className="size-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="center">
          <Calendar
            mode="single"
            selected={date}
            defaultMonth={date}
            onSelect={handleDateSelect}
            disabled={(d) => d > today}
            autoFocus
            locale={ja}
            modifiers={marker ? { marked: marker.matcher } : undefined}
            modifiersClassNames={{ marked: MARKER_CLASS_NAME }}
          />
          {marker && (
            <div className="flex items-center gap-1.5 border-t px-3 py-2 text-xs text-muted-foreground">
              <span className="size-1.5 rounded-full bg-emerald-500" />
              {marker.label}
            </div>
          )}
          {!isToday && (
            <div className="border-t px-3 py-2">
              <Button variant="ghost" size="sm" className="w-full" onClick={handleGoToToday}>
                今日に移動
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
      <Button variant="ghost" size="icon" className="size-8" onClick={goToNextDay} disabled={isToday}>
        <ArrowRight className="size-4" />
      </Button>
    </div>
  )
}
